import type { FormConfig } from '@workwave-tidal/form-fairy'
import type { FormFields, FormErrors } from '../formFields'

import { validateAddTenantSource } from '@/features/domain/gps'
import { selectIsTelematicsUnavailable } from '@/features/domain/account'
import { type AppDispatch, store } from '@/store'

import { useTexts } from '../useTexts'

export function createGeotabFormValidation(
  dispatch: AppDispatch,
  texts: ReturnType<typeof useTexts>,
): FormConfig<FormFields, FormErrors>['validations'] {
  return [
    {
      id: 'labelValidator',
      fields: 'label',
      validator: formApi => {
        const state = store.getState()

        const { tenantSources } = state.domain.publicData.domain.gps.telematics
        const { value } = formApi.getField('label')

        const labelRequiredError: FormErrors = {
          id: 'labelRequiredError',
          field: 'label',
          message: texts.geotab.errors.labelRequired,
        }

        const empty = value.trim() === ''

        const labelUniqueError: FormErrors = {
          id: 'uniqueLabelError',
          field: 'label',
          message: texts.geotab.errors.labelUnique,
        }

        const unique = Object.values(tenantSources).every(source => source.label !== value)

        return {
          [labelRequiredError.id]: empty ? labelRequiredError : null,
          [labelUniqueError.id]: !unique ? labelUniqueError : null,
        }
      },
    },
    {
      id: 'credentialsValidator',
      fields: 'credentials',
      validator: async formApi => {
        const { value: wrongCredentials } = formApi.getField('wrongCredentials')
        const { value: credentials } = formApi.getField('credentials')
        const { value: sourceId } = formApi.getField('sourceId')
        const { value: label } = formApi.getField('label')

        const { value } = formApi.getField('credentials')

        const usernameRequiredError: FormErrors = {
          id: 'usernameRequired',
          field: 'credentials',
          index: 0,
          message: texts.geotab.errors.usernameRequired,
        }

        const passwordRequiredError: FormErrors = {
          id: 'passwordRequired',
          field: 'credentials',
          index: 1,
          message: texts.geotab.errors.passwordRequired,
        }

        const databaseRequiredError: FormErrors = {
          id: 'databaseRequired',
          field: 'credentials',
          index: 2,
          message: texts.geotab.errors.databaseRequired,
        }

        const username = value.find(v => v.fieldName === 'username')
        const password = value.find(v => v.fieldName === 'password')
        const database = value.find(v => v.fieldName === 'database')

        const usernameEmpty = username?.value.trim() === ''
        const passwordEmpty = password?.value.trim() === ''
        const databaseEmpty = database?.value.trim() === ''

        const wrongCredentialsUsernameError: FormErrors = {
          id: 'wrongCredentialsUsernameError',
          field: 'credentials',
          index: 0,
          message: texts.geotab.errors.usernameIncorrect,
        }

        const wrongCredentialsPasswordError: FormErrors = {
          id: 'wrongCredentialsPasswordError',
          field: 'credentials',
          index: 1,
          message: texts.geotab.errors.passwordIncorrect,
        }

        const wrongCredentialsDatabaseError: FormErrors = {
          id: 'wrongCredentialsDatabaseError',
          field: 'credentials',
          index: 2,
          message: texts.geotab.errors.databaseIncorrect,
        }

        if (usernameEmpty || passwordEmpty || databaseEmpty || wrongCredentials) {
          return {
            [usernameRequiredError.id]: usernameEmpty ? usernameRequiredError : null,
            [passwordRequiredError.id]: passwordEmpty ? passwordRequiredError : null,
            [databaseRequiredError.id]: databaseEmpty ? databaseRequiredError : null,
            [wrongCredentialsUsernameError.id]: wrongCredentials
              ? wrongCredentialsUsernameError
              : null,
            [wrongCredentialsPasswordError.id]: wrongCredentials
              ? wrongCredentialsPasswordError
              : null,
            [wrongCredentialsDatabaseError.id]: wrongCredentials
              ? wrongCredentialsDatabaseError
              : null,
            credentialsAlreadyInUse: null,
          }
        }

        // Skip remote validation when telematics is unavailable.
        // The submit will fail and surface the generic toast via useOnSubmit.
        if (selectIsTelematicsUnavailable(store.getState())) {
          return {
            [usernameRequiredError.id]: null,
            [passwordRequiredError.id]: null,
            [databaseRequiredError.id]: null,
            [wrongCredentialsUsernameError.id]: null,
            [wrongCredentialsPasswordError.id]: null,
            [wrongCredentialsDatabaseError.id]: null,
            credentialsAlreadyInUse: null,
          }
        }

        const response = await dispatch(
          validateAddTenantSource({
            credentials,
            sourceId,
            label,
          }),
        )

        if (validateAddTenantSource.rejected.match(response)) {
          const errorCode =
            response.payload?.type === 'rpcFailure' ? response.payload.error.errorCode : undefined

          if (errorCode === 101) {
            // An integration to this account already exist
            return {
              [usernameRequiredError.id]: null,
              [passwordRequiredError.id]: null,
              [databaseRequiredError.id]: null,
              [wrongCredentialsUsernameError.id]: null,
              [wrongCredentialsPasswordError.id]: null,
              [wrongCredentialsDatabaseError.id]: null,
              credentialsAlreadyInUse: {
                id: 'credentialsAlreadyInUse',
                field: 'credentials',
                index: 0,
                message: texts.geotab.errors.credentialsAlreadyInUse,
              },
            }
          }

          throw new Error(response.payload?.message ?? texts.serviceUnavailableGenericError)
        }

        return {
          [usernameRequiredError.id]: null,
          [passwordRequiredError.id]: null,
          [databaseRequiredError.id]: null,
          [wrongCredentialsUsernameError.id]: null,
          [wrongCredentialsPasswordError.id]: null,
          [wrongCredentialsDatabaseError.id]: null,
          credentialsAlreadyInUse: null,
        }
      },
    },
  ]
}
